import React, { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import {
  confirmForgotPassword,
  forgotPassword,
  resetForgotPasswordState,
  selectForgotPassword,
} from "store/slices/authSlice";
import { toast } from "react-hot-toast";
import AuthLayout from "components/AuthLayout/AuthLayout";
import { motion } from "framer-motion";
import OtpInput from "components/OtpInputComponent/OtpInput";
import { validatePassword } from "utils/passwordValidator";
import LucideIcon from "utils/LucideIcon";
import { authPageMeta } from "data/authPages.enum";
import { IAccountResetPass } from "Interfaces/interface.auth";
import { useAppDispatch } from "store/hooks/hooks";

const inputClasses = `
  block w-full border-neutral-200 focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 
  bg-white dark:border-neutral-700 dark:focus:ring-primary-6000 dark:focus:ring-opacity-25 dark:bg-neutral-900 
  disabled:bg-neutral-200 dark:disabled:bg-neutral-800 rounded-2xl text-sm font-normal h-11 px-4 py-3
`;

const validateForm = (values: IAccountResetPass) => {
  const errors: Partial<IAccountResetPass> = {};
  if (!values.code || values.code.length !== 6) {
    errors.code = "Please enter a valid 6-digit code";
  }
  const passwordError = validatePassword(values.newPassword);
  if (passwordError) {
    errors.newPassword = passwordError;
  }
  if (!values.confirmPassword) {
    errors.confirmPassword = "Confirm Password is required";
  } else if (values.confirmPassword !== values.newPassword) {
    errors.confirmPassword = "Passwords must match";
  }
  return errors;
};

const PageResetPassword: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const { loading } = useSelector(selectForgotPassword);

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300);
  const [canResend, setCanResend] = useState(false);
  const [resendTrigger, setResendTrigger] = useState(0);
  const formInitValues: IAccountResetPass = {
    code: "",
    newPassword: "",
    confirmPassword: "",
  };

  useEffect(() => {
    if (!email) {
      toast.error("Missing email. Redirecting to forgot password.");
      setTimeout(() => navigate("/forgot-password"), 2000);
      return;
    }
    let timer: NodeJS.Timeout | null = null;
    if (timeLeft > 0 && !loading) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            if (timer) clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (timeLeft === 0 && !loading) {
      setCanResend(true);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [timeLeft, loading, resendTrigger, email, navigate]);

  const handleResendCode = async () => {
    if (email) {
      try {
        await dispatch(forgotPassword({ email })).unwrap();
      } catch (err: any) {
        console.log("err: ", err);
      }
    }
  };

  const handleSubmit = async (
    values: IAccountResetPass,
    { setSubmitting, setErrors }: any
  ) => {
    setSubmitting(true);
    try {
      await dispatch(
        confirmForgotPassword({
          email,
          code: values.code,
          newPassword: values.newPassword,
        })
      ).unwrap();
      navigate("/signin");
    } catch (err: any) {
      setErrors({ form: err.message || "Failed to reset password." });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetForgotPasswordState());
    };
  }, [dispatch]);

  return (
    <AuthLayout {...authPageMeta.reset}>
      <Helmet>
        <title>Reset Password || 360CUSTOMIZER</title>
      </Helmet>
      <motion.div
        className="w-full flex justify-center items-center"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" as const, delay: 0.2 }}
      >
        <div className="bg-white dark:bg-neutral-800 rounded-2xl w-full max-w-md p-6">
          <h2 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100 mb-4 text-center font-display">
            Reset Password
          </h2>
          <Formik
            initialValues={formInitValues}
            validate={validateForm}
            onSubmit={handleSubmit}
          >
            {({
              isSubmitting,
              values,
              setFieldValue,
              setFieldTouched,
              errors,
            }) => (
              <Form className="grid grid-cols-1 gap-6">
                <label className="block">
                  <span className="text-neutral-800 dark:text-neutral-200 mb-1 inline-block">
                    Verification Code
                  </span>
                  <Field name="code">
                    {({ field, form }: any) => (
                      <OtpInput
                        value={field.value}
                        disabled={loading || isSubmitting}
                        onChange={(val) => {
                          setFieldValue("code", val);
                          setFieldTouched("code", true, false); // Touch without triggering immediate validation
                          if (val.length === 6) {
                            // Clear error if 6 digits are entered
                            if (errors.code) {
                              setFieldValue("code", val, true); // Trigger validation to clear error
                            }
                          }
                        }}
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name="code"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </label>
                <label className="block">
                  <span className="text-neutral-800 dark:text-neutral-200">
                    New Password
                  </span>
                  <div className="relative">
                    <Field
                      id="newPassword"
                      name="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      className={`mt-1 disabled:cursor-not-allowed disabled:opacity-50 ${inputClasses}`}
                      disabled={loading || isSubmitting}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue("newPassword", e.target.value);
                        if (
                          validatePassword(e.target.value) === null &&
                          errors.newPassword
                        ) {
                          setFieldValue("newPassword", e.target.value, true); // Clear error if valid
                        }
                      }}
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-primary-500 hover:text-primary-600 text-xs font-body">
                      <LucideIcon
                        name={showNewPassword ? "Eye" : "EyeOff"}
                        className="p-1 cursor-pointer"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      />
                    </div>
                  </div>
                  <ErrorMessage
                    name="newPassword"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </label>
                <label className="block">
                  <span className="text-neutral-800 dark:text-neutral-200">
                    Confirm Password
                  </span>
                  <div className="relative">
                    <Field
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm new password"
                      className={`mt-1 disabled:cursor-not-allowed disabled:opacity-50 ${inputClasses}`}
                      disabled={loading || isSubmitting}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue("confirmPassword", e.target.value);
                        if (
                          e.target.value === values.newPassword &&
                          errors.confirmPassword
                        ) {
                          setFieldValue(
                            "confirmPassword",
                            e.target.value,
                            true
                          ); // Clear error if matches
                        }
                      }}
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-primary-500 hover:text-primary-600 text-xs font-body">
                      <LucideIcon
                        name={showConfirmPassword ? "Eye" : "EyeOff"}
                        className="p-1 cursor-pointer"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      />
                    </div>
                  </div>
                  <ErrorMessage
                    name="confirmPassword"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </label>
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <ButtonPrimary
                    type="submit"
                    disabled={loading || isSubmitting}
                    loading={loading || isSubmitting}
                    className="text-white font-semibold py-2 rounded-xl shadow-md transition-colors duration-300 hover:scale-105 ease-in text-sm disabled:cursor-not-allowed disabled:text-gray-800"
                    sizeClass="w-full"
                  >
                    {!loading && !isSubmitting && "Reset Password"}
                  </ButtonPrimary>
                </motion.div>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={loading}
                    className={`text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-300 ${
                      loading ? "opacity-50 pointer-events-none" : ""
                    }`}
                  >
                    Resend Code{" "}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
          <p className="block text-center text-neutral-700 dark:text-neutral-300 mt-6">
            Remember your password?{" "}
            <Link
              className={`text-primary-500 hover:text-primary-600 font-medium hover:underline transition-colors duration-300
                ${loading && "pointer-events-none opacity-50"}
                `}
              to="/signin"
            >
              Sign In
            </Link>
          </p>
        </div>
      </motion.div>
    </AuthLayout>
  );
};

export default PageResetPassword;
